import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import api from "./api";
import { reinitialiserModules } from "./modules";
import {
  ContexteMarque,
  appliquerMarque,
  chargerMarque,
  marqueEnCache,
  reinitialiserMarque,
} from "./marque";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import VehiculesListe from "./pages/VehiculesListe";
import VehiculeFiche from "./pages/VehiculeFiche";
import VehiculeForm from "./pages/VehiculeForm";
import OperationForm from "./pages/OperationForm";
import VgpWizard from "./pages/VgpWizard";
import CtWizard from "./pages/CtWizard";
import Reglages from "./pages/Reglages";
import Stock from "./pages/Stock";
import Financement from "./pages/Financement";
import LocationWizard from "./pages/LocationWizard";
import MesLocations from "./pages/MesLocations";
import LimiteErreur from "./components/LimiteErreur";
import Inspections from "./pages/Inspections";
import PresentationModule from "./pages/PresentationModule";
import MonCompte from "./pages/MonCompte";
import MotDePasseOublie from "./pages/MotDePasseOublie";
import Reinitialiser from "./pages/Reinitialiser";
import Utilisateurs from "./pages/Utilisateurs";
import Plateforme from "./pages/Plateforme";
import PageIntrouvable from "./pages/PageIntrouvable";

function App() {
  const [user, setUser] = useState(() => {
    const brut = localStorage.getItem("user");
    return brut ? JSON.parse(brut) : null;
  });

  const [marque, setMarque] = useState(marqueEnCache);

  // La marque en cache est appliquee tout de suite (pas de clignotement aux
  // couleurs par defaut au rafraichissement), puis rafraichie depuis le
  // serveur qui reste la source de verite.
  useEffect(() => {
    if (!user) return;
    appliquerMarque(marque);
    chargerMarque()
      .then(setMarque)
      .catch(() => {
        /* Un echec ici n'a rien de bloquant : l'application reste utilisable
           avec les couleurs en cache, ou neutres. */
      });
    // Volontairement sur `user` seul : recharger a chaque changement de
    // `marque` bouclerait.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const connecter = (token, utilisateur) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(utilisateur));
    localStorage.removeItem("consultation");
    // LES MODULES SOUSCRITS SE RELISENT A CHAQUE CONNEXION.
    //
    // Leur cache vit en mémoire, le temps de la session. Sans cette remise à
    // zéro, une déconnexion suivie d'une reconnexion — sans rechargement de
    // page, ce qui est le geste NORMAL dans une application de ce type —
    // réaffichait les modules d'AVANT.
    //
    // Signalé par Yanis le 18/08 : l'éditeur active un module, le client se
    // reconnecte, et son écran le montre toujours grisé. Il fallait actualiser
    // la page pour le voir apparaître.
    //
    // Le défaut est plus vicieux qu'un simple retard d'affichage : le client
    // vient de PAYER un module, on lui dit qu'il est activé, et son logiciel
    // affiche un cadenas. C'est le genre de détail après lequel il ne croit
    // plus ce que l'écran lui raconte.
    reinitialiserModules();
    setUser(utilisateur);
  };

  // Consultation d'un client par le super-admin : le jeton rendu par le
  // serveur remplace le jeton courant. Toutes les requetes suivantes portent
  // sur ce client, en lecture seule -- c'est le serveur qui l'impose.
  const entrerConsultation = (token, client) => {
    localStorage.setItem("token", token);
    localStorage.setItem("consultation", JSON.stringify(client));
  };

  const quitterConsultation = useCallback(() => {
    api
      .post("/plateforme/quitter-consultation")
      .then(({ data }) => {
        localStorage.setItem("token", data.access_token);
        localStorage.removeItem("consultation");
        chargerMarque().catch(() => {});
        window.location.assign("/plateforme");
      })
      .catch(() => {
        // Le jeton de consultation ne vaut plus rien : on repart proprement.
        deconnecter();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le bandeau de consultation vit dans Layout, monte par chaque page : il
  // previent par un evenement plutot que de faire descendre une fonction dans
  // les douze routes.
  useEffect(() => {
    window.addEventListener("quitter-consultation", quitterConsultation);
    return () =>
      window.removeEventListener("quitter-consultation", quitterConsultation);
  }, [quitterConsultation]);

  // Hors consultation, le super-admin n'a aucune donnee metier : son
  // organisation ne porte pas de flotte. Les ecrans metier le renvoient donc
  // a ses clients plutot que de lui afficher des zeros. Lu depuis le stockage
  // et non d'un etat React : entrer et sortir de consultation rechargent la
  // page, la valeur est donc toujours fraiche au rendu.
  const estPlateforme =
    user?.role === "superadmin" && !localStorage.getItem("consultation");

  /** Ecran metier : login si absent, clients si super-admin chez lui. */
  const ecranMetier = (element) => {
    if (!user) return <Navigate to="/login" />;
    if (estPlateforme) return <Navigate to="/plateforme" replace />;
    return element;
  };

  const deconnecter = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("consultation");
    // Les couleurs du client repartent avec lui : l'ecran de connexion ne
    // doit pas garder la charte du dernier passe sur ce navigateur.
    reinitialiserMarque();
    // Les modules aussi : sur un poste partagé, le suivant à se connecter ne
    // doit rien hériter de la souscription du précédent.
    reinitialiserModules();
    setMarque(marqueEnCache());
    setUser(null);
  };

  return (
    <ContexteMarque.Provider value={marque}>
      <BrowserRouter>
      <LimiteErreur>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/" /> : <Login onLogin={connecter} />}
        />
        {/* Les deux ecrans du mot de passe oublie sont accessibles SANS
            session -- c'est tout le probleme qu'ils resolvent. Ils restent
            atteignables meme connecte : quelqu'un qui suit un lien recu par
            e-mail depuis un poste deja ouvert doit pouvoir aller au bout. */}
        <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
        <Route path="/reinitialiser" element={<Reinitialiser />} />
        {/* LE TABLEAU DE BORD N'EST PAS POUR TOUT LE MONDE (24/09/2026).
            Il affiche les coûts de la flotte ; le mécanicien ne les voit pas.
            Comme c'est la page d'ACCUEIL, il ne suffit pas de retirer le lien
            du menu : il faut lui donner un autre point d'arrivée, sinon il se
            connecte et tombe sur un écran vide ou une erreur.

            Les données, elles, sont refusées par le serveur
            (`auth.MODULES_PAR_ROLE` ferme le module « operations », donc
            `/couts/synthese`). Cette redirection est du confort — elle évite
            un écran cassé, elle ne protège rien. */}
        <Route
          path="/"
          element={
            user?.role === "mecanicien"
              ? <Navigate to="/inspections" replace />
              : ecranMetier(<Dashboard user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules"
          element={
            ecranMetier(<VehiculesListe user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules/nouveau"
          element={
            ecranMetier(<VehiculeForm user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules/:vehiculeId/location/nouvelle"
          element={
            ecranMetier(<LocationWizard user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/locations"
          element={
            user ? <MesLocations user={user} onLogout={deconnecter} /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/locations/:locationId"
          element={
            ecranMetier(<LocationWizard user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules/:vehiculeId/operations/nouvelle"
          element={
            ecranMetier(<OperationForm user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules/:vehiculeId/operations/:operationId"
          element={
            ecranMetier(<OperationForm user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules/:vehiculeId/vgp/:inspectionId"
          element={
            ecranMetier(<VgpWizard user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules/:vehiculeId/ct/:inspectionId"
          element={
            ecranMetier(<CtWizard user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules/:id"
          element={
            ecranMetier(<VehiculeFiche user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/vehicules/:id/modifier"
          element={
            ecranMetier(<VehiculeForm user={user} onLogout={deconnecter} />)
          }
        />
        <Route
          path="/stock"
          element={
            user ? <Stock user={user} onLogout={deconnecter} /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/financement"
          element={
            user ? <Financement user={user} onLogout={deconnecter} /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/inspections"
          element={
            user ? <Inspections user={user} onLogout={deconnecter} /> : <Navigate to="/login" />
          }
        />
        {/* Page d'un module NON souscrit : statique, elle n'appelle aucune
            route du module. C'est ce qui rend tenable de montrer un module
            qu'on n'a pas -- un ecran verrouille qui interrogerait le serveur
            afficherait une erreur au lieu d'une offre. */}
        <Route
          path="/modules/:nom"
          element={
            user ? (
              <PresentationModule user={user} onLogout={deconnecter} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/mon-compte"
          element={
            user ? <MonCompte user={user} onLogout={deconnecter} /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/utilisateurs"
          element={
            user ? <Utilisateurs user={user} onLogout={deconnecter} /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/reglages"
          element={
            user ? <Reglages user={user} onLogout={deconnecter} /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/plateforme"
          element={
            user ? (
              <Plateforme
                user={user}
                onLogout={deconnecter}
                onConsulter={entrerConsultation}
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* UNE ADRESSE INCONNUE LE DIT (audit B3, 28/09). Elle renvoyait en
            silence a l'accueil. Sans compte, on va se connecter : la page
            introuvable n'a rien a montrer a qui n'est pas entre. */}
        <Route
          path="*"
          element={
            user
              ? <PageIntrouvable user={user} onLogout={deconnecter} />
              : <Navigate to="/login" />
          }
        />
      </Routes>
      </LimiteErreur>
      </BrowserRouter>
    </ContexteMarque.Provider>
  );
}

export default App;
